import { useEffect, useRef } from 'react';
import type { MouseEvent, PointerEvent } from 'react';

const MIN_DRAG_DISTANCE = 20;

type PillDragOptions = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Drag direction that opens the content: 'down' for top headers, 'up' for bottom sheets. */
  openDirection: 'up' | 'down';
  /** Pass false to ignore new gestures and cancel one in progress. */
  enabled?: boolean;
  /** Called when a gesture starts, before the pointer has moved. */
  onDragStart?: () => void;
  /** Called while the pointer moves past the drag threshold. `distance` is the signed clientY delta from the start. */
  onDragMove?: (distance: number, startedOpen: boolean) => void;
  /** Called when a gesture ends or is cancelled, before `onOpenChange`. */
  onDragEnd?: () => void;
};

/**
 * Pointer-drag and click handling for a pill toggle button.
 * Only the primary pointer is captured, drags need more than 20px, and the click that follows a drag is ignored.
 * Cancelled gestures restore the previous state; short movements and keyboard activation toggle normally.
 */
export const usePillDrag = ({
  isOpen,
  onOpenChange,
  openDirection,
  enabled = true,
  onDragStart,
  onDragMove,
  onDragEnd,
}: PillDragOptions) => {
  const dragRef = useRef<{
    pointerId: number;
    startY: number;
    isOpen: boolean;
    moved: boolean;
    button: HTMLButtonElement;
  } | null>(null);
  const suppressClick = useRef(false);

  const releaseCapture = (drag: NonNullable<typeof dragRef.current>) => {
    if (drag.button.hasPointerCapture(drag.pointerId)) {
      drag.button.releasePointerCapture(drag.pointerId);
    }
  };

  const opensWith = (distance: number) => (openDirection === 'down' ? distance > 0 : distance < 0);

  useEffect(() => {
    if (!enabled && dragRef.current) {
      const drag = dragRef.current;
      dragRef.current = null;
      suppressClick.current = false;
      onDragEnd?.();
      onOpenChange(drag.isOpen);
      releaseCapture(drag);
    }
  }, [enabled, onOpenChange, onDragEnd]);

  useEffect(
    () => () => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (drag) {
        releaseCapture(drag);
      }
    },
    [],
  );

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (!enabled || event.button !== 0 || !event.isPrimary || dragRef.current) {
      return;
    }
    event.stopPropagation();
    suppressClick.current = false;
    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      isOpen,
      moved: false,
      button: event.currentTarget,
    };
    onDragStart?.();
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const distance = event.clientY - drag.startY;
    if (Math.abs(distance) > MIN_DRAG_DISTANCE) {
      drag.moved = true;
    }
    if (drag.moved) {
      onDragMove?.(distance, drag.isOpen);
    }
  };

  const onPointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const distance = event.clientY - drag.startY;
    const moved = drag.moved || Math.abs(distance) > MIN_DRAG_DISTANCE;
    suppressClick.current = moved;
    dragRef.current = null;
    onDragEnd?.();
    if (moved) {
      onOpenChange(Math.abs(distance) > MIN_DRAG_DISTANCE ? opensWith(distance) : drag.isOpen);
    }
    releaseCapture(drag);
  };

  const onPointerCancel = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    dragRef.current = null;
    onDragEnd?.();
    suppressClick.current = drag.moved;
    onOpenChange(drag.isOpen);
    releaseCapture(drag);
  };

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Captured releases can synthesize a click; keyboard activation has detail 0.
    const suppressed = suppressClick.current && event.detail !== 0;
    suppressClick.current = false;
    if (!suppressed) {
      onOpenChange(!isOpen);
    }
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onLostPointerCapture: onPointerCancel,
    onClick,
  };
};
